import { Modal, IconButton, TextField, Button, MenuItem } from "@mui/material"
import { z } from "zod"
import ClearIcon from "@mui/icons-material/Clear"
import { useAddModel } from "@/hooks/useAddModel"
import useLocations from "@/hooks/useLocations"
import { useEffect } from "react"
import { useWatch } from "react-hook-form"
import { useQueryClient } from "@tanstack/react-query"

const LocationSchema = z
  .object({
    name: z.string().min(1, "Name is required"),
    type: z.enum(["country", "district", "city"]),
    parent_id: z.number().nullable().optional(),
  })
  .refine((data) => data.type === "country" || !!data.parent_id, {
    message: "Parent location is required",
    path: ["parent_id"],
  })

const PARENT_TYPE = {
  country: null,
  district: "country",
  city: "district",
}

const TYPE_OPTIONS = [
  { value: "country", label: "Country" },
  { value: "district", label: "District" },
  { value: "city", label: "City" },
]

const AddLocationModal = ({ open, onClose }) => {
  const {
    branchId,
    branchSelector,
    errors,
    openDialog,
    isSubmitting,
    selectedRow,
    control,
    Controller,
    register,
    handleSubmit,
    onSubmit,
    handleReset,
    defaultOnClose,
    reset,
    newRecord,
  } = useAddModel(
    "location",
    LocationSchema,
    "location",
    undefined,
    (data) => ({
      ...data,
      parent_id: data.type === "country" ? null : data.parent_id,
    })
  )

  const { locations } = useLocations()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (newRecord) {
      queryClient.invalidateQueries({ queryKey: ["location-all"] })
    }
  }, [newRecord, queryClient])

  const selectedType = useWatch({ control, name: "type" })
  const parentType = PARENT_TYPE[selectedType]
  const parentOptions = locations.filter(
    (loc) =>
      loc.type === parentType && (!branchId || loc.branch_id === branchId)
  )

  useEffect(() => {
    if (selectedRow) {
      reset({
        name: selectedRow.name,
        type: selectedRow.type,
        parent_id: selectedRow.parent_id,
      })
    } else {
      reset({ name: "", type: "country", parent_id: null })
    }
  }, [selectedRow, reset])

  return (
    <Modal open={open ? open : openDialog} onClose={defaultOnClose}>
      <div className="w-[95vw] sm:w-[90vw] md:w-[500px] mx-auto mt-24 bg-white rounded-lg shadow-md">
        {/* Header */}
        <div className="flex items-center justify-between bg-[#f6f7f9] px-4 py-3 rounded-t-lg">
          <h2 className="font-semibold text-black text-[16px] md:text-[18px]">
            {selectedRow ? "Update Location" : "Add New Location"}
          </h2>
          <IconButton
            aria-label="remove"
            size="small"
            onClick={onClose ? onClose : defaultOnClose}
          >
            <ClearIcon />
          </IconButton>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="p-4">
          {branchSelector && <div className="mb-4">{branchSelector}</div>}
          {/* Type Field */}
          <div className="mb-4">
            <Controller
              name="type"
              control={control}
              defaultValue="country"
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Type"
                  error={!!errors.type}
                  helperText={errors.type?.message}
                >
                  {TYPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </div>

          {/* Parent Field */}
          {parentType && (
            <div className="mb-4">
              <Controller
                name="parent_id"
                control={control}
                defaultValue={null}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label={`Parent (${parentType})`}
                    value={field.value ?? ""}
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? null : Number(e.target.value)
                      )
                    }
                    error={!!errors.parent_id}
                    helperText={errors.parent_id?.message}
                  >
                    {parentOptions.length === 0 && (
                      <MenuItem value="" disabled>
                        No {parentType} found — add one first
                      </MenuItem>
                    )}
                    {parentOptions.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </div>
          )}

          {/* Name Field */}
          <div className="mb-4">
            <TextField
              fullWidth
              label="Name"
              error={!!errors.name}
              helperText={errors.name?.message}
              {...register("name")}
            />
          </div>

          {/* Button Row */}
          <div className="flex justify-end items-center gap-2 mt-2 flex-wrap">
            <Button
              onClick={handleReset}
              variant="text"
              className="capitalize rounded-md font-medium text-[13px] py-[11px] px-[30px] w-full sm:w-auto"
            >
              Reset
            </Button>

            <Button
              onClick={onClose ? onClose : defaultOnClose}
              variant="outlined"
              color="error"
              className="capitalize rounded-md font-medium text-[13px] py-[11px] px-[30px] w-full sm:w-auto"
            >
              Cancel
            </Button>

            <Button
              type="submit"
              disabled={isSubmitting}
              variant="contained"
              className="capitalize rounded-md font-medium text-[13px] py-[11px] px-[30px] text-white w-full sm:w-auto"
            >
              {selectedRow ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default AddLocationModal
